import { useCallback, useEffect, useRef, useState } from "react";

// VdoCipher mints the otp/playbackInfo pair server-side with a short TTL (five
// minutes by default) and refuses an expired pair with "error 2013 — embed code
// time expired". The pair reaches us inside the lecture/course payload, so it
// starts ageing at page load: a student who opened the page, read for a while
// and only then pressed play hit 2013 on a perfectly valid video.
//
// The otp is only needed to *start* playback — once the player has a licence it
// keeps streaming — so the fix is to guarantee a young pair at mount time, not
// to keep one alive forever. This component refuses to mount the player on a
// stale pair, asks its owner to re-mint, and swaps the fresh pair in while the
// player is still idle (invisible to the student). A player the student has
// already engaged with is never re-mounted: that would restart the video.
//
// "Engaged" is read from VdoCipher's own player SDK (real playback events), so
// a video that is actually playing can never be torn down by the freshness
// logic. The SDK is a third-party script that can fail to load; when it does,
// we fall back to a focus heuristic (see `onBlur` below).
const MAX_CIPHER_AGE_MS = 3 * 60 * 1000;
const FRESHNESS_CHECK_MS = 30 * 1000;

const SDK_SRC = "https://player.vdocipher.com/v2/api.js";

// Only events that cannot fire on an untouched player. `loadedmetadata` and
// friends are deliberately absent: they fire while the student is still idle.
const PLAYBACK_EVENTS = ["play", "playing", "timeupdate", "seeking"] as const;

type VdoVideo = {
  currentTime: number;
  duration: number;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
};

type VdoPlayerInstance = { video?: VdoVideo };

/** Fraction of duration that counts as watched (e.g. 0.8 = 80%). */
const DEFAULT_WATCH_THRESHOLD = 0.8;

declare global {
  interface Window {
    VdoPlayer?: {
      getInstance: (iframe: HTMLIFrameElement) => VdoPlayerInstance | undefined;
    };
  }
}

let sdkPromise: Promise<void> | null = null;

/** Loads VdoCipher's player API once per document. */
const loadPlayerSdk = (): Promise<void> => {
  if (window.VdoPlayer) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SDK_SRC}"]`
    );
    if (existing?.dataset.loaded === "true") {
      resolve();
      return;
    }

    const script = existing ?? document.createElement("script");
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    });
    script.addEventListener("error", () =>
      reject(new Error("VdoCipher player SDK failed to load"))
    );

    if (!existing) {
      script.src = SDK_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  }).catch((error) => {
    // Let a later mount retry rather than caching the failure forever.
    sdkPromise = null;
    throw error;
  });

  return sdkPromise;
};

type Props = {
  // Changing this resets engagement tracking — it is a different video.
  videoId: string;
  otp: string;
  playbackInfo: string;
  // When the payload carrying this pair was fetched (react-query's
  // dataUpdatedAt). The pair's age is measured from here.
  cipherFetchedAt: number;
  // Re-mint the pair by refetching that payload.
  onRefresh: () => void;
  // Fired once when playback reaches `watchThreshold` of the video duration.
  onWatchThreshold?: () => void;
  // Fired when the video finishes playing.
  onEnded?: () => void;
  // Fraction of duration that counts as watched. Defaults to 0.8 (80%).
  watchThreshold?: number;
  className?: string;
};

const VdoCipherPlayer = ({
  videoId,
  otp,
  playbackInfo,
  cipherFetchedAt,
  onRefresh,
  onWatchThreshold,
  onEnded,
  watchThreshold = DEFAULT_WATCH_THRESHOLD,
  className = "",
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // The pair the mounted iframe was built from. It deliberately lags the props
  // so a background refresh can't swap the src under a running player.
  const [mounted, setMounted] = useState({ otp, playbackInfo });
  const [engaged, setEngaged] = useState(false);
  const [stale, setStale] = useState(false);
  // Bumped on every iframe `load`. `getInstance` can be called before the frame
  // is ready, so the subscription is retried once the player is actually there.
  const [frameLoads, setFrameLoads] = useState(0);

  // True once the SDK is driving engagement for the current iframe. While it
  // is, the focus heuristic stays out of the way — it can only produce false
  // positives, which cost the student a 2013 error when they finally press play.
  const sdkAttachedRef = useRef(false);
  // One auto-watched call per mounted video, even if timeupdate keeps firing.
  const thresholdFiredRef = useRef(false);

  // Held in a ref so the freshness effect doesn't resubscribe whenever the
  // owner passes a new callback identity.
  const refreshRef = useRef(onRefresh);
  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  const watchThresholdRef = useRef(onWatchThreshold);
  useEffect(() => {
    watchThresholdRef.current = onWatchThreshold;
  }, [onWatchThreshold]);

  const endedRef = useRef(onEnded);
  useEffect(() => {
    endedRef.current = onEnded;
  }, [onEnded]);

  const watchThresholdRatioRef = useRef(watchThreshold);
  useEffect(() => {
    watchThresholdRatioRef.current = watchThreshold;
  }, [watchThreshold]);

  const markEngaged = useCallback(() => setEngaged(true), []);

  const handleTimeUpdate = useCallback(
    (video: VdoVideo) => {
      markEngaged();
      if (thresholdFiredRef.current || !watchThresholdRef.current) return;

      const { currentTime, duration } = video;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (currentTime / duration < watchThresholdRatioRef.current) return;

      thresholdFiredRef.current = true;
      watchThresholdRef.current();
    },
    [markEngaged]
  );

  // Whether the iframe is on screen this render, and the identity of the frame
  // itself. Both drive the SDK subscription: it must re-attach to every new
  // frame, and there is nothing to attach to while the pair is being re-minted.
  const showPlayer = !(stale && !engaged);
  const frameKey = `${videoId}-${mounted.otp}`;

  // Real playback signal. `getInstance` wraps the already-rendered iframe, so
  // this runs after the frame for `frameKey` is in the DOM.
  useEffect(() => {
    if (!showPlayer) return;

    let cancelled = false;
    let detach: (() => void) | undefined;

    loadPlayerSdk()
      .then(() => {
        if (cancelled) return;
        const iframe = iframeRef.current;
        if (!iframe || !window.VdoPlayer) return;

        const video = window.VdoPlayer.getInstance(iframe)?.video;
        if (!video) return;

        const onTimeUpdate = () => handleTimeUpdate(video);
        const onVideoEnded = () => {
          markEngaged();
          endedRef.current?.();
        };

        PLAYBACK_EVENTS.forEach((event) => {
          if (event === "timeupdate") {
            video.addEventListener(event, onTimeUpdate);
          } else {
            video.addEventListener(event, markEngaged);
          }
        });
        video.addEventListener("ended", onVideoEnded);
        sdkAttachedRef.current = true;
        detach = () => {
          PLAYBACK_EVENTS.forEach((event) => {
            if (event === "timeupdate") {
              video.removeEventListener(event, onTimeUpdate);
            } else {
              video.removeEventListener(event, markEngaged);
            }
          });
          video.removeEventListener("ended", onVideoEnded);
        };
      })
      .catch(() => {
        // Blocked or offline: the focus heuristic below stays in charge.
      });

    return () => {
      cancelled = true;
      sdkAttachedRef.current = false;
      detach?.();
    };
  }, [frameKey, showPlayer, frameLoads, markEngaged, handleTimeUpdate]);

  // Fallback only. A click inside a cross-origin iframe blurs the parent
  // document and leaves the iframe as the active element. It errs the safe way:
  // it can mark an idle player engaged (we then stop refreshing it), never the
  // reverse (we never restart a playing one).
  useEffect(() => {
    const onBlur = () => {
      if (sdkAttachedRef.current) return;
      if (document.activeElement === iframeRef.current) setEngaged(true);
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, []);

  // A different video is a fresh start in every sense.
  useEffect(() => {
    setEngaged(false);
    thresholdFiredRef.current = false;
    setMounted({ otp, playbackInfo });
    // Only videoId drives this: adopting the new pair is the point, and
    // re-running on otp changes would fight the guarded adoption below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Adopt a re-minted pair, but only while nothing is playing.
  useEffect(() => {
    if (engaged) return;
    setMounted((cur) =>
      cur.otp === otp && cur.playbackInfo === playbackInfo
        ? cur
        : { otp, playbackInfo }
    );
  }, [otp, playbackInfo, engaged]);

  // Watch the pair age. Re-minting is skipped for a hidden tab (no one can
  // press play) and for an engaged player, and runs again the moment the tab
  // comes back — which is exactly the "left it open for an hour" case.
  useEffect(() => {
    if (engaged) return;

    const check = () => {
      const expired = Date.now() - cipherFetchedAt >= MAX_CIPHER_AGE_MS;
      setStale(expired);
      if (expired && document.visibilityState === "visible") {
        refreshRef.current();
      }
    };

    check();
    const timer = setInterval(check, FRESHNESS_CHECK_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [cipherFetchedAt, engaged]);

  // Mounting the player on an expired pair only produces the 2013 error screen,
  // so hold the frame until the refresh lands. This window is the length of one
  // refetch and only opens on a page that has been sitting idle.
  if (!showPlayer) {
    return (
      <div
        className={`flex items-center justify-center bg-black/90 text-white/70 text-sm ${className}`}
      >
        Preparing video...
      </div>
    );
  }

  return (
    <iframe
      // Re-minting must remount the frame, not just retarget it.
      key={frameKey}
      ref={iframeRef}
      onLoad={() => setFrameLoads((n) => n + 1)}
      src={`https://player.vdocipher.com/v2/?otp=${mounted.otp}&playbackInfo=${mounted.playbackInfo}`}
      className={className}
      allow="encrypted-media"
      allowFullScreen
    />
  );
};

export default VdoCipherPlayer;
